import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  ColorPicker,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Switch,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import axios from 'axios';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../api/client';
import { authApi, categoriesApi, skladsApi, storesApi } from '../api/endpoints';
import { invalidateMoneyData, queryKeys } from '../api/queryKeys';
import type { ExpenseCategory, Sklad, Store } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { AmountInput, optionalAmountRule } from '../components/AmountInput';
import { ErrorBlock, LoadingBlock } from '../components/PageStates';
import { formatKopeks, formatRubTyping, kopeksToRubString, rubToKopeks } from '../lib/money';
import { HistoryTab } from './settings/HistoryTab';
import { MoyskladTab } from './settings/MoyskladTab';
import { UsersTab } from './settings/UsersTab';

/** ColorPicker feeds the form either a hex string (initial value) or a Color
 * object (after a pick); `null` when cleared. */
type ColorValue = string | { toHexString(): string } | null;

interface DirectoryFormValues {
  name: string;
  opening?: string;
  sortOrder?: number;
  color?: ColorValue;
  // Store-only rates, entered in PERCENT (2.5), stored as basis points (250).
  terminalFeePercent?: number;
  taxPercent?: number;
  isArchived?: boolean;
}

/** Normalize the ColorPicker form value to a «#rrggbb» string, or null. */
function colorToHex(v: ColorValue | undefined): string | null {
  if (!v) return null;
  if (typeof v === 'string') return v || null;
  return typeof v.toHexString === 'function' ? v.toHexString() : null;
}

/**
 * Stores / sklads CRUD tab (STEP-07). openingBalance/openingDebt are edited
 * in RUBLES and converted to kopeks at the boundary. Archiving an entity
 * that still carries money asks for explicit confirmation.
 */
function DirectoryTab({ kind }: { kind: 'store' | 'sklad' }): JSX.Element {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Store | Sklad | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<DirectoryFormValues>();

  const query = useQuery<(Store | Sklad)[]>({
    queryKey: kind === 'store' ? queryKeys.stores(true) : queryKeys.sklads(true),
    queryFn: () => (kind === 'store' ? storesApi.list(true) : skladsApi.list(true)),
  });

  const moneyOf = (item: Store | Sklad): number =>
    kind === 'store' ? (item as Store).balance : (item as Sklad).debt;
  const openingOf = (item: Store | Sklad): number =>
    kind === 'store' ? (item as Store).openingBalance : (item as Sklad).openingDebt;

  const saveMutation = useMutation<Store | Sklad, Error, DirectoryFormValues>({
    mutationFn: (values: DirectoryFormValues) => {
      const opening = values.opening && values.opening !== '-' ? rubToKopeks(values.opening) : 0;
      const common = { name: values.name.trim(), sortOrder: values.sortOrder ?? 0 };
      if (kind === 'store') {
        const body = {
          ...common,
          openingBalance: opening,
          terminalFeeBps: Math.round((values.terminalFeePercent ?? 0) * 100),
          taxBps: Math.round((values.taxPercent ?? 0) * 100),
        };
        return editing
          ? storesApi.update(editing.id, { ...body, isArchived: values.isArchived ?? false })
          : storesApi.create(body);
      }
      const body = { ...common, openingDebt: opening, color: colorToHex(values.color) };
      return editing
        ? skladsApi.update(editing.id, { ...body, isArchived: values.isArchived ?? false })
        : skladsApi.create(body);
    },
    onSuccess: () => {
      message.success(t('common.saved'));
      invalidateMoneyData(queryClient);
      setEditing(null);
      setCreating(false);
    },
    onError: (error) => message.error(apiErrorMessage(error) ?? t('common.requestError')),
  });

  const submit = (values: DirectoryFormValues): void => {
    const archivingWithMoney =
      editing !== null &&
      values.isArchived === true &&
      !editing.isArchived &&
      moneyOf(editing) !== 0;
    if (archivingWithMoney) {
      modal.confirm({
        title: t('settings.archiveNonZeroTitle'),
        content: t(
          kind === 'store' ? 'settings.archiveNonZeroStore' : 'settings.archiveNonZeroSklad',
          {
            balance: formatKopeks(moneyOf(editing)),
          },
        ),
        okText: t('common.save'),
        cancelText: t('common.cancel'),
        onOk: () => saveMutation.mutateAsync(values).then(() => undefined),
      });
      return;
    }
    saveMutation.mutate(values);
  };

  const openModal = (item: Store | Sklad | null): void => {
    setEditing(item);
    setCreating(item === null);
    form.setFieldsValue({
      name: item?.name ?? '',
      opening: item ? formatRubTyping(kopeksToRubString(openingOf(item)), true) : '',
      sortOrder: item?.sortOrder ?? 0,
      color: kind === 'sklad' ? ((item as Sklad | null)?.color ?? null) : null,
      terminalFeePercent: kind === 'store' && item ? (item as Store).terminalFeeBps / 100 : 0,
      taxPercent: kind === 'store' && item ? (item as Store).taxBps / 100 : 0,
      isArchived: item?.isArchived ?? false,
    });
  };

  const modalOpen = creating || editing !== null;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => openModal(null)}>
        {t(kind === 'store' ? 'settings.addStore' : 'settings.addSklad')}
      </Button>

      {query.isLoading ? (
        <LoadingBlock />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => void query.refetch()} />
      ) : (
        <Card size="small" styles={{ body: { paddingTop: 0, paddingBottom: 0 } }}>
          <List
            dataSource={query.data}
            renderItem={(item) => (
              <List.Item
                style={{ paddingInline: 4 }}
                extra={
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => openModal(item)}
                    aria-label={t('common.edit')}
                  />
                }
              >
                <List.Item.Meta
                  title={
                    <>
                      {kind === 'sklad' && (item as Sklad).color ? (
                        <span
                          aria-hidden
                          style={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: 3,
                            background: (item as Sklad).color ?? undefined,
                            marginInlineEnd: 8,
                            verticalAlign: 'middle',
                          }}
                        />
                      ) : null}
                      {item.name}
                      {item.isArchived ? (
                        <Tag style={{ marginInlineStart: 8 }}>{t('common.archivedTag')}</Tag>
                      ) : null}
                    </>
                  }
                  description={t(
                    kind === 'store' ? 'settings.currentBalance' : 'settings.currentDebt',
                    {
                      balance: formatKopeks(moneyOf(item)),
                    },
                  )}
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      <Modal
        open={modalOpen}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        title={editing?.name ?? t(kind === 'store' ? 'settings.addStore' : 'settings.addSklad')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={saveMutation.isPending}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form<DirectoryFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={submit}
        >
          <Form.Item
            name="name"
            label={t('settings.name')}
            rules={[{ required: true, whitespace: true, message: t('common.nameRequired') }]}
          >
            <Input size="large" maxLength={200} />
          </Form.Item>
          <Form.Item
            name="opening"
            label={t(kind === 'store' ? 'settings.openingBalance' : 'settings.openingDebt')}
            extra={t('settings.openingHint')}
            rules={[optionalAmountRule(t)]}
          >
            <AmountInput allowNegative />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('settings.sortOrder')}>
            <InputNumber size="large" style={{ width: '100%' }} step={1} />
          </Form.Item>
          {kind === 'sklad' ? (
            <Form.Item name="color" label={t('settings.color')} extra={t('settings.colorHint')}>
              <ColorPicker format="hex" allowClear size="large" showText />
            </Form.Item>
          ) : null}
          {kind === 'store' ? (
            <>
              <Form.Item
                name="terminalFeePercent"
                label={t('settings.terminalFeePercent')}
                extra={t('settings.feeTaxHint')}
              >
                <InputNumber
                  size="large"
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  step={0.1}
                  decimalSeparator=","
                />
              </Form.Item>
              <Form.Item name="taxPercent" label={t('settings.taxPercent')}>
                <InputNumber
                  size="large"
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  step={0.1}
                  decimalSeparator=","
                />
              </Form.Item>
            </>
          ) : null}
          {editing ? (
            <Form.Item
              name="isArchived"
              label={t('settings.archivedSwitch')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>
    </div>
  );
}

interface CategoryFormValues {
  name: string;
  sortOrder?: number;
  isArchived?: boolean;
}

function CategoriesTab(): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ExpenseCategory | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<CategoryFormValues>();

  const query = useQuery({
    queryKey: queryKeys.categories(true),
    queryFn: () => categoriesApi.list(true),
  });

  const saveMutation = useMutation({
    mutationFn: (values: CategoryFormValues) => {
      const body = { name: values.name.trim(), sortOrder: values.sortOrder ?? 0 };
      return editing
        ? categoriesApi.update(editing.id, { ...body, isArchived: values.isArchived ?? false })
        : categoriesApi.create(body);
    },
    onSuccess: () => {
      message.success(t('common.saved'));
      void queryClient.invalidateQueries({ queryKey: ['categories'] });
      setEditing(null);
      setCreating(false);
    },
    onError: (error) => message.error(apiErrorMessage(error) ?? t('common.requestError')),
  });

  const openModal = (item: ExpenseCategory | null): void => {
    setEditing(item);
    setCreating(item === null);
    form.setFieldsValue({
      name: item?.name ?? '',
      sortOrder: item?.sortOrder ?? 0,
      isArchived: item?.isArchived ?? false,
    });
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => openModal(null)}>
        {t('settings.addCategory')}
      </Button>
      {query.isLoading ? (
        <LoadingBlock />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => void query.refetch()} />
      ) : query.data.length === 0 ? (
        <Typography.Text type="secondary">{t('settings.categoriesEmpty')}</Typography.Text>
      ) : (
        <Card size="small" styles={{ body: { paddingTop: 0, paddingBottom: 0 } }}>
          <List
            dataSource={query.data}
            renderItem={(item) => (
              <List.Item
                style={{ paddingInline: 4 }}
                extra={
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => openModal(item)}
                    aria-label={t('common.edit')}
                  />
                }
              >
                <Typography.Text>
                  {item.name}
                  {item.isArchived ? (
                    <Tag style={{ marginInlineStart: 8 }}>{t('common.archivedTag')}</Tag>
                  ) : null}
                </Typography.Text>
              </List.Item>
            )}
          />
        </Card>
      )}

      <Modal
        open={creating || editing !== null}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        title={editing?.name ?? t('settings.addCategory')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={saveMutation.isPending}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form<CategoryFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => saveMutation.mutate(values)}
        >
          <Form.Item
            name="name"
            label={t('settings.name')}
            rules={[{ required: true, whitespace: true, message: t('common.nameRequired') }]}
          >
            <Input size="large" maxLength={200} />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('settings.sortOrder')}>
            <InputNumber size="large" style={{ width: '100%' }} step={1} />
          </Form.Item>
          {editing ? (
            <Form.Item
              name="isArchived"
              label={t('settings.archivedSwitch')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>
    </div>
  );
}

interface PasswordFormValues {
  oldPassword: string;
  newPassword: string;
  newPasswordRepeat: string;
}

function PasswordTab(): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { dropSession } = useAuth();

  const mutation = useMutation({
    mutationFn: (values: PasswordFormValues) =>
      authApi.changePassword(values.oldPassword, values.newPassword),
    onSuccess: () => {
      // The backend revokes every refresh token on password change — the
      // local session is dead; sign out cleanly and let the guard redirect.
      message.success(t('settings.passwordChanged'), 5);
      dropSession();
    },
    onError: (error) => {
      const wrongOld = axios.isAxiosError(error) && error.response?.status === 401;
      message.error(
        wrongOld
          ? t('settings.passwordWrongOld')
          : (apiErrorMessage(error) ?? t('common.requestError')),
      );
    },
  });

  return (
    <Card size="small">
      <Form<PasswordFormValues>
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => mutation.mutate(values)}
      >
        <Form.Item
          name="oldPassword"
          label={t('settings.oldPassword')}
          rules={[{ required: true, message: t('settings.passwordRequired') }]}
        >
          <Input.Password size="large" autoComplete="current-password" />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label={t('settings.newPassword')}
          rules={[
            { required: true, message: t('settings.passwordRequired') },
            { min: 8, message: t('settings.passwordMin') },
          ]}
        >
          <Input.Password size="large" autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="newPasswordRepeat"
          label={t('settings.newPasswordRepeat')}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('settings.passwordRequired') },
            ({ getFieldValue }) => ({
              validator: (_rule, value: string) =>
                !value || value === getFieldValue('newPassword')
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('settings.passwordMismatch'))),
            }),
          ]}
        >
          <Input.Password size="large" autoComplete="new-password" />
        </Form.Item>
        <Button type="primary" htmlType="submit" size="large" loading={mutation.isPending} block>
          {t('settings.passwordChange')}
        </Button>
      </Form>
    </Card>
  );
}

export function SettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const { user, isStoreRestricted, isSuperadmin } = useAuth();
  // Only businesses the MoySklad account feeds get the tab; elsewhere it is noise
  // (and the backend answers 403 MOYSKLAD_NOT_ENABLED anyway).
  const moysklad = user?.business?.moyskladEnabled === true;
  return (
    <Tabs
      defaultActiveKey="stores"
      // Tabs must FIT a 360px phone — overflowed antd tabs hide behind a
      // fiddly «...» dropdown the owner should never need.
      size="small"
      tabBarGutter={12}
      items={[
        { key: 'stores', label: t('settings.storesTab'), children: <DirectoryTab kind="store" /> },
        // Sklads carry the whole-business debt — not for a store-restricted USER.
        ...(isStoreRestricted
          ? []
          : [
              {
                key: 'sklads',
                label: t('settings.skladsTab'),
                children: <DirectoryTab kind="sklad" />,
              },
            ]),
        { key: 'categories', label: t('settings.categoriesTab'), children: <CategoriesTab /> },
        ...(moysklad
          ? [{ key: 'moysklad', label: t('settings.moyskladTab'), children: <MoyskladTab /> }]
          : []),
        { key: 'password', label: t('settings.passwordTab'), children: <PasswordTab /> },
        // Superadmin-only: user management + the «Кто что сделал» activity log.
        ...(isSuperadmin
          ? [
              { key: 'users', label: t('settings.usersTab'), children: <UsersTab /> },
              { key: 'history', label: t('settings.historyTab'), children: <HistoryTab /> },
            ]
          : []),
      ]}
    />
  );
}
